import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsFillPencilFill, BsFillTrashFill } from 'react-icons/bs';
import { getCurrentUser } from '../utils/auth';
import './ProtocolUsage.css';
import './ModalWide.css';

const REQUEST_HEADERS = {
  machine: '2',
  'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
};

const buildModalState = (row) => ({
  protocol_detail_id: row?.protocol_detail_id ?? null,
  protocol_id: row?.protocol?.protocol_id ? String(row.protocol.protocol_id) : '',
  protocol_name: row?.protocol?.name ?? '',
  detail_description: row?.detail_description ?? '',
  details_uses: row?.details_uses ?? row?.detail_uses ?? '',
});

const ProtocolUsage = () => {
  const navigate = useNavigate();
  const tokenPayload = getCurrentUser() || {};
  const tokenCustomerId = Number(tokenPayload?.customer ?? tokenPayload?.customer_id ?? 0);
  const currentUserTypeId = Number(tokenPayload?.user_type_id ?? tokenPayload?.user_type);
  const isCreator = currentUserTypeId === 10;

  const [records, setRecords] = useState([]);
  const [allProtocols, setAllProtocols] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchText, setSearchText] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalState, setModalState] = useState(buildModalState());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [detailPendingDelete, setDetailPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasValidCustomerId = Number.isInteger(tokenCustomerId) && tokenCustomerId > 0;
  const isEditMode = modalState.protocol_detail_id !== null;

  const fetchProtocolDetails = useCallback(async () => {
    if (!isCreator && !hasValidCustomerId) {
      setRecords([]);
      setIsTableLoading(false);
      setErrorMessage('Current user is not assigned to a customer.');
      return;
    }

    setIsTableLoading(true);
    setErrorMessage('');

    try {
      const endpoint = isCreator
        ? '/protocol_details/'
        : '/protocol_details/customer/' + tokenCustomerId;

      const response = await fetch(process.env.REACT_APP_API_URL_BASE + endpoint, {
        method: 'GET',
        headers: REQUEST_HEADERS,
      });

      if (!response.ok) {
        throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
      }

      const data = await response.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setRecords([]);
      setErrorMessage('Unable to load protocol details.');
    } finally {
      setIsTableLoading(false);
    }
  }, [hasValidCustomerId, isCreator, tokenCustomerId]);

  const fetchAllProtocols = useCallback(async () => {
    if (!isCreator) {
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocols/view/active', {
        method: 'GET',
        headers: REQUEST_HEADERS,
      });

      if (!response.ok) {
        throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
      }

      const data = await response.json();
      const options = (Array.isArray(data) ? data : []).map((protocol) => ({
        id: protocol?.protocol_id ?? protocol?.id,
        name: protocol?.name ?? '',
      })).filter((protocol) => protocol.id !== undefined && protocol.id !== null && protocol.name);

      setAllProtocols(options);
    } catch (error) {
      console.error(error);
      setAllProtocols([]);
    }
  }, [isCreator]);

  useEffect(() => {
    if (!window.sessionStorage.getItem('auth')) {
      navigate('/unauthorized');
      return;
    }

    fetchProtocolDetails();
    fetchAllProtocols();
  }, [fetchAllProtocols, fetchProtocolDetails, navigate]);

  const normalizedSearchText = String(searchText ?? '').trim().toLowerCase();

  const visibleRecords = useMemo(() => {
    const filteredRecords = !normalizedSearchText
      ? records
      : records.filter((record) => {
          const searchFields = [
            record?.protocol?.name,
            record?.detail_description,
            record?.details_uses ?? record?.detail_uses,
          ];

          return searchFields
            .filter((value) => value !== undefined && value !== null)
            .some((value) => String(value).toLowerCase().includes(normalizedSearchText));
        });

    return [...filteredRecords].sort((a, b) => {
      const aName = String(a?.protocol?.name ?? '').toLowerCase();
      const bName = String(b?.protocol?.name ?? '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [records, normalizedSearchText]);

  const usedProtocolIds = useMemo(() => {
    return new Set(
      records
        .map((record) => Number(record?.protocol?.protocol_id))
        .filter((protocolId) => Number.isInteger(protocolId) && protocolId > 0)
    );
  }, [records]);

  const addProtocolOptions = useMemo(() => {
    return allProtocols.filter((protocol) => {
      const protocolId = Number(protocol?.id);
      return Number.isInteger(protocolId) && protocolId > 0 && !usedProtocolIds.has(protocolId);
    });
  }, [allProtocols, usedProtocolIds]);

  const openAddModal = () => {
    setModalError('');

    const defaultProtocolId = addProtocolOptions[0]?.id ? String(addProtocolOptions[0].id) : '';
    setModalState({
      ...buildModalState(),
      protocol_id: defaultProtocolId,
      protocol_name: '',
    });

    if (!defaultProtocolId) {
      setModalError('All active protocols already have protocol details entries.');
    }

    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setModalError('');
    setModalState(buildModalState(record));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) {
      return;
    }

    setIsModalOpen(false);
    setModalError('');
    setModalState(buildModalState());
  };

  const openDeleteDialog = (record) => {
    setDetailPendingDelete(record);
    setShowDeleteConfirm(true);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) {
      return;
    }

    setShowDeleteConfirm(false);
    setDetailPendingDelete(null);
  };

  const handleDeleteConfirm = async () => {
    const protocolDetailId = Number(detailPendingDelete?.protocol_detail_id);
    if (!Number.isInteger(protocolDetailId) || protocolDetailId <= 0) {
      closeDeleteDialog();
      return;
    }

    setIsDeleting(true);

    try {
      const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_details/protocol_detail/' + protocolDetailId, {
        method: 'DELETE',
        headers: REQUEST_HEADERS,
      });

      if (!response.ok) {
        throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
      }

      await fetchProtocolDetails();
      closeDeleteDialog();
    } catch (error) {
      console.error(error);
      setErrorMessage('Unable to remove protocol details.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalChange = (event) => {
    const { name, value } = event.target;
    setModalState((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (isSaving) {
      return;
    }

    const protocolId = Number(modalState.protocol_id);
    const description = String(modalState.detail_description ?? '').trim();
    const uses = String(modalState.details_uses ?? '').trim();

    if (!Number.isInteger(protocolId) || protocolId <= 0 || !description || !uses) {
      setModalError('Protocol, Description, and Best Used For are required.');
      return;
    }

    if (!isEditMode && addProtocolOptions.length === 0) {
      setModalError('All active protocols already have protocol details entries.');
      return;
    }

    setIsSaving(true);
    setModalError('');

    const payload = {
      protocol: protocolId,
      detail_description: description,
      details_uses: uses,
    };

    try {
      const endpoint = isEditMode
        ? '/protocol_details/protocol_detail/' + modalState.protocol_detail_id
        : '/protocol_details/';
      const method = isEditMode ? 'PATCH' : 'POST';

      const response = await fetch(process.env.REACT_APP_API_URL_BASE + endpoint, {
        method,
        headers: {
          ...REQUEST_HEADERS,
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
      }

      await fetchProtocolDetails();
      closeModal();
    } catch (error) {
      console.error(error);
      setModalError('Unable to save protocol details.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='table-wrapper protocol-details-page'>
      <h3 className='page-title protocol-details-title'>
        Protocol Usage Information <span className='page-count'>{visibleRecords.length}</span>
      </h3>

      <div className='protocol-details-controls'>
        {isCreator && (
          <button type='button' className='btn protocol-details-add-btn' onClick={openAddModal}>
            + Add New Protocol Details
          </button>
        )}
        <div className='protocol-details-search-wrap'>
          <input
            type='text'
            className='protocol-details-search-input'
            placeholder='Search protocol usage'
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            aria-label='Search protocol usage details'
          />
        </div>
      </div>

      {errorMessage && <div className='error protocol-details-error'>{errorMessage}</div>}

      <div className='protocol-details-table-wrap'>
        <table className='table protocol-details-table'>
          <thead>
            <tr>
              <th className='left'>Protocol Name</th>
              <th className='left'>Description</th>
              <th className='left'>Best Used For</th>
              {isCreator && <th className='left'>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isTableLoading ? (
              Array.from({ length: 6 }).map((_, rowIndex) => (
                <tr key={`protocol-details-skeleton-${rowIndex}`} className='table-skeleton-row'>
                  {Array.from({ length: isCreator ? 4 : 3 }).map((__, cellIndex) => (
                    <td key={`protocol-details-skeleton-${rowIndex}-${cellIndex}`}>
                      <span className={`table-skeleton-line${(isCreator && cellIndex === 3) ? ' short' : ''}`} />
                    </td>
                  ))}
                </tr>
              ))
            ) : visibleRecords.length === 0 ? (
              <tr>
                <td className='left' colSpan={isCreator ? 4 : 3}>
                  {normalizedSearchText ? 'No matching protocol details found.' : 'No protocol details found for this customer.'}
                </td>
              </tr>
            ) : (
              visibleRecords.map((record) => (
                <tr key={record.protocol_detail_id}>
                  <td className='left'>{record?.protocol?.name ?? ''}</td>
                  <td className='left protocol-details-wrap'>{record?.detail_description ?? ''}</td>
                  <td className='left protocol-details-wrap'>{record?.details_uses ?? record?.detail_uses ?? ''}</td>
                  {isCreator && (
                    <td className='actions-cell'>
                      <span className='actions'>
                        <BsFillPencilFill
                          className='edit-btn'
                          title='Edit Protocol Details'
                          aria-label='Edit Protocol Details'
                          onClick={() => openEditModal(record)}
                        />
                        <BsFillTrashFill
                          className='protocol-details-delete-btn'
                          title='Remove Protocol Details'
                          aria-label='Remove Protocol Details'
                          onClick={() => openDeleteDialog(record)}
                        />
                      </span>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className='modal-container' onClick={(event) => {
          if (event.target.className === 'modal-container') {
            closeModal();
          }
        }}>
          <div className='modal-edit protocol-details-modal'>
            <button className='modal-close-btn' onClick={closeModal} aria-label='Close protocol details modal' disabled={isSaving}>X</button>
            <form className='protocol-details-modal-form' onSubmit={handleSave}>
              <h3 className='protocol-details-modal-title'>{isEditMode ? 'Edit Protocol Details' : 'Add Protocol Details'}</h3>
              <p className='protocol-details-modal-subtitle'>Configure the protocol mapping and usage guidance shown on the Support page.</p>

              {!isEditMode && (
                <div className='form-group protocol-details-form-group'>
                  <label htmlFor='protocol_id'>Protocol*</label>
                  <select
                    id='protocol_id'
                    name='protocol_id'
                    value={modalState.protocol_id}
                    onChange={handleModalChange}
                    disabled={isSaving || addProtocolOptions.length === 0}
                  >
                    <option value=''>Select Protocol</option>
                    {addProtocolOptions.map((protocol) => (
                      <option key={protocol.id} value={protocol.id}>
                        {protocol.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isEditMode && (
                <div className='form-group protocol-details-form-group'>
                  <label htmlFor='protocol_name'>Protocol</label>
                  <input
                    id='protocol_name'
                    name='protocol_name'
                    value={modalState.protocol_name}
                    readOnly
                    disabled
                  />
                </div>
              )}

              <div className='form-group protocol-details-form-group'>
                <label htmlFor='detail_description'>Description*</label>
                <textarea
                  id='detail_description'
                  name='detail_description'
                  value={modalState.detail_description}
                  onChange={handleModalChange}
                  rows={4}
                  disabled={isSaving}
                />
              </div>

              <div className='form-group protocol-details-form-group'>
                <label htmlFor='details_uses'>Best Used For*</label>
                <textarea
                  id='details_uses'
                  name='details_uses'
                  value={modalState.details_uses}
                  onChange={handleModalChange}
                  rows={4}
                  disabled={isSaving}
                />
              </div>

              {modalError && <div className='error protocol-details-error'>{modalError}</div>}

              <div className='protocol-details-modal-actions'>
                <button type='button' className='btn btn-cancel' onClick={closeModal} disabled={isSaving}>Cancel</button>
                <button type='submit' className='btn' disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className='deactivate-modal-backdrop' role='dialog' aria-modal='true'>
          <div className='deactivate-modal-card'>
            <h3 className='deactivate-modal-header'>Remove Protocol Details?</h3>
            <p className='deactivate-modal-text'>
              Are you sure you want to remove
              {' '}
              <strong>{detailPendingDelete?.protocol?.name ?? 'this protocol detail'}</strong>
              ?
            </p>
            <div className='deactivate-modal-actions'>
              <button type='button' className='btn btn-cancel' onClick={closeDeleteDialog} disabled={isDeleting}>Cancel</button>
              <button type='button' className='btn btn-danger' onClick={handleDeleteConfirm} disabled={isDeleting}>
                {isDeleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtocolUsage;
